// components/SetSpecialPriceModal.jsx
import React, { useState, useEffect } from 'react';
import { X, DollarSign, AlertCircle, Loader, Trash2 } from 'lucide-react';
import { useToast } from './ToastContainer';
import api from '../../api';

const SetSpecialPriceModal = ({ isOpen, onClose, tenant, plans, onSubmit, isSubmitting }) => {
  const { error: showError, success: showSuccess } = useToast();
  
  const [formData, setFormData] = useState({
    planId: '',
    specialPrice: '',
    reason: '',
    validFrom: '',
    validTo: '',
    billingCycle: 'yearly'
  });

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [currentSpecialPrice, setCurrentSpecialPrice] = useState(null);
  const [allSpecialPrices, setAllSpecialPrices] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [hasExistingSpecialPrice, setHasExistingSpecialPrice] = useState(false);
  const [isRemovingSpecific, setIsRemovingSpecific] = useState(null);

  // ✅ Fetch ALL special prices for this tenant
  useEffect(() => {
    const fetchAllSpecialPrices = async () => {
      if (!isOpen || !tenant?.tenantId) return;
      
      setIsFetching(true);
      
      try {
        const response = await api.get(
          `/api/super-admin/subscription/tenant/${tenant.tenantId}/special-price`
        );
        
        if (response.data.success && response.data.data) {
          const data = response.data.data;
          setAllSpecialPrices(data.allSpecialPrices || []);
          
          // ✅ If there's an active special price for the selected plan, show it
          const planId = formData.planId || tenant.planId;
          if (planId) {
            const activeForPlan = data.allSpecialPrices?.find(
              sp => sp.planId === planId && sp.isActive === true
            );
            
            if (activeForPlan) {
              setCurrentSpecialPrice(activeForPlan);
              setHasExistingSpecialPrice(true);
              
              setFormData(prev => ({
                ...prev,
                planId: activeForPlan.planId,
                specialPrice: activeForPlan.specialPrice?.amount || '',
                reason: activeForPlan.reason || '',
                validFrom: activeForPlan.validFrom ? 
                  new Date(activeForPlan.validFrom).toISOString().split('T')[0] : '',
                validTo: activeForPlan.validTo ? 
                  new Date(activeForPlan.validTo).toISOString().split('T')[0] : '',
                billingCycle: activeForPlan.specialPrice?.billingCycle || 'yearly'
              }));
              
              const plan = plans.find(p => p.planId === activeForPlan.planId);
              if (plan) setSelectedPlan(plan);
            } else {
              setCurrentSpecialPrice(null);
              setHasExistingSpecialPrice(false);
              
              // If tenant has a plan, set it
              if (tenant.planId) {
                setFormData(prev => ({
                  ...prev,
                  planId: tenant.planId
                }));
                const plan = plans.find(p => p.planId === tenant.planId);
                if (plan) setSelectedPlan(plan);
              }
            }
          }
        }
      } catch (err) {
        if (err.response?.status !== 404) {
          console.error('❌ Error fetching special prices:', err);
          showError('Failed to fetch special prices');
        }
      } finally {
        setIsFetching(false);
      }
    };

    fetchAllSpecialPrices();
  }, [isOpen, tenant?.tenantId, tenant?.planId, plans, showError, formData.planId]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentSpecialPrice(null);
      setHasExistingSpecialPrice(false);
      setAllSpecialPrices([]);
      setSelectedPlan(null);
      setIsRemovingSpecific(null);
      setFormData({
        planId: '',
        specialPrice: '',
        reason: '',
        validFrom: '',
        validTo: '',
        billingCycle: 'yearly'
      });
    }
  }, [isOpen]);

  if (!isOpen || !tenant) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'planId') {
      const plan = plans.find(p => p.planId === value);
      setSelectedPlan(plan);
      
      // Check if this plan already has a special price
      const existing = allSpecialPrices.find(sp => sp.planId === value && sp.isActive === true);
      if (existing) {
        setHasExistingSpecialPrice(true);
        setCurrentSpecialPrice(existing);
        setFormData(prev => ({
          ...prev,
          planId: existing.planId,
          specialPrice: existing.specialPrice?.amount || '',
          reason: existing.reason || '',
          validFrom: existing.validFrom ? 
            new Date(existing.validFrom).toISOString().split('T')[0] : '',
          validTo: existing.validTo ? 
            new Date(existing.validTo).toISOString().split('T')[0] : '',
          billingCycle: existing.specialPrice?.billingCycle || 'yearly'
        }));
      } else {
        setHasExistingSpecialPrice(false);
        setCurrentSpecialPrice(null);
        setFormData(prev => ({
          ...prev,
          specialPrice: '',
          reason: '',
          validFrom: '',
          validTo: '',
          billingCycle: 'yearly'
        }));
      }
    }
  };

  // ✅ Handle Remove Special Price for a specific plan
  const handleRemoveSpecialPrice = async (planIdToRemove) => {
    const planName = selectedPlan?.name || 'this plan';
    
    if (!window.confirm(`Are you sure you want to remove the special price for "${planName}"?`)) {
      return;
    }

    setIsRemovingSpecific(planIdToRemove);
    try {
      const response = await api.delete(
        `/api/super-admin/subscription/tenant/${tenant.tenantId}/special-price`,
        {
          data: { planId: planIdToRemove }
        }
      );

      if (response.data.success) {
        showSuccess('Special price removed successfully');
        
        // ✅ Update local state
        setHasExistingSpecialPrice(false);
        setCurrentSpecialPrice(null);
        setFormData(prev => ({
          ...prev,
          specialPrice: '',
          reason: '',
          validFrom: '',
          validTo: '',
          billingCycle: 'yearly'
        }));
        
        // ✅ Refresh the list
        const refreshResponse = await api.get(
          `/api/super-admin/subscription/tenant/${tenant.tenantId}/special-price`
        );
        if (refreshResponse.data.success) {
          setAllSpecialPrices(refreshResponse.data.data.allSpecialPrices || []);
        }
        
        // ✅ Close modal after a delay
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        showError(response.data.message || 'Failed to remove special price');
      }
    } catch (err) {
      console.error('❌ Error removing special price:', err);
      showError(err.response?.data?.message || 'Failed to remove special price');
    } finally {
      setIsRemovingSpecific(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.planId) {
      showError('Please select a plan');
      return;
    }
    
    if (!formData.specialPrice || parseFloat(formData.specialPrice) < 0) {
      showError('Please enter a valid special price');
      return;
    }
    
    if (!formData.reason) {
      showError('Please provide a reason for special pricing');
      return;
    }

    onSubmit(tenant.tenantId, {
      planId: formData.planId,
      specialPrice: parseFloat(formData.specialPrice),
      reason: formData.reason,
      validFrom: formData.validFrom || undefined,
      validTo: formData.validTo || undefined,
      billingCycle: formData.billingCycle
    });
  };

  const originalPrice = selectedPlan?.pricing?.yearly?.amount || 0;
  const specialPriceNum = parseFloat(formData.specialPrice) || 0;
  const discount = originalPrice - specialPriceNum;
  const discountPercentage = originalPrice > 0 ? Math.round((discount / originalPrice) * 100) : 0;

  const isUpdate = hasExistingSpecialPrice && currentSpecialPrice?.isActive === true;

  // ✅ Render existing special prices list with remove buttons
  const renderSpecialPricesList = () => {
    if (allSpecialPrices.length === 0) return null;

    return (
      <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
        <p className="text-xs font-semibold text-slate-600 mb-2">Existing Special Prices</p>
        <div className="space-y-1.5">
          {allSpecialPrices.map((sp) => (
            <div key={sp._id} className="flex items-center justify-between text-xs p-1.5 bg-white rounded border border-slate-100">
              <div className="flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full ${sp.isActive ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                <span className="font-medium">{sp.planName || sp.planId}</span>
                <span className="text-slate-500">₹{sp.specialPrice?.amount || 0}</span>
                {sp.isActive && (
                  <span className="text-[10px] text-emerald-600 font-medium">Active</span>
                )}
                {!sp.isActive && (
                  <span className="text-[10px] text-slate-400">Inactive</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {sp.validTo && (
                  <span className="text-[10px] text-slate-400">
                    Valid until {new Date(sp.validTo).toLocaleDateString()}
                  </span>
                )}
                {/* ✅ Remove button for each special price */}
                {sp.isActive && (
                  <button
                    onClick={() => handleRemoveSpecialPrice(sp.planId)}
                    disabled={isRemovingSpecific === sp.planId}
                    className="text-rose-500 hover:text-rose-700 transition-colors disabled:opacity-50"
                    title="Remove special price"
                  >
                    {isRemovingSpecific === sp.planId ? (
                      <Loader className="w-3 h-3 animate-spin" />
                    ) : (
                      <Trash2 className="w-3 h-3" />
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ✅ Check if selected plan has special price
  const selectedPlanHasSpecialPrice = allSpecialPrices.some(
    sp => sp.planId === formData.planId && sp.isActive === true
  );

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              {isUpdate ? 'Update Special Price' : 'Set Special Price'}
            </h2>
            <p className="text-sm text-slate-500">
              {tenant.tenantName || tenant.tenantId}
              <span className="ml-2 text-xs text-slate-400">
                {allSpecialPrices.length} special price(s) configured
              </span>
            </p>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          {isFetching ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-8 h-8 text-indigo-600 animate-spin" />
              <span className="ml-2 text-sm text-slate-500">Loading special prices...</span>
            </div>
          ) : (
            <>
              {/* ✅ Show all existing special prices with remove buttons */}
              {renderSpecialPricesList()}

              {/* Plan Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Select Plan <span className="text-red-500">*</span>
                </label>
                <select
                  name="planId"
                  value={formData.planId}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
                  required
                >
                  <option value="">Select a plan</option>
                  {plans.filter(p => p.isActive).map((plan) => {
                    const hasPrice = allSpecialPrices.some(sp => sp.planId === plan.planId && sp.isActive);
                    return (
                      <option key={plan.planId} value={plan.planId}>
                        {plan.name} - ₹{plan.pricing?.yearly?.amount || 0}/year
                        {hasPrice && ' ⭐ Special Price'}
                      </option>
                    );
                  })}
                </select>
                {selectedPlan && (
                  <div className="mt-1 text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                    <span>
                      Plan: {selectedPlan.name}
                      {selectedPlan.trial?.enabled && ` • ${selectedPlan.trial.durationDays} days trial`}
                    </span>
                    {selectedPlanHasSpecialPrice && (
                      <span className="text-emerald-600 font-medium">✅ Special price exists</span>
                    )}
                    {!selectedPlanHasSpecialPrice && formData.planId && (
                      <span className="text-amber-600 font-medium">⚠️ No special price for this plan</span>
                    )}
                  </div>
                )}
              </div>

              {/* Special Price Input */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Special Price (₹) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="specialPrice"
                  value={formData.specialPrice}
                  onChange={handleChange}
                  min="0"
                  step="1"
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
                  placeholder="Enter special price"
                  required
                />
                {selectedPlan && specialPriceNum > 0 && (
                  <div className="mt-2 flex items-center gap-3 text-sm flex-wrap">
                    <span className="text-slate-500">Original: ₹{originalPrice.toLocaleString()}</span>
                    <span className="text-slate-300">→</span>
                    <span className="text-emerald-600 font-medium">₹{specialPriceNum.toLocaleString()}</span>
                    {discount > 0 && (
                      <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">
                        {discountPercentage}% off
                      </span>
                    )}
                    {isUpdate && (
                      <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                        Updating
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  rows="2"
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
                  placeholder="e.g., Volume discount, Beta customer, Promotional offer"
                  required
                />
              </div>

              {/* Validity */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Valid From
                  </label>
                  <input
                    type="date"
                    name="validFrom"
                    value={formData.validFrom}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Valid To
                  </label>
                  <input
                    type="date"
                    name="validTo"
                    value={formData.validTo}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
                  />
                </div>
              </div>

              {/* Billing Cycle */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Billing Cycle
                </label>
                <select
                  name="billingCycle"
                  value={formData.billingCycle}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                  <option value="one_time">One Time</option>
                </select>
              </div>

              {/* Info Box */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-blue-700">
                    <p className="font-semibold">📝 Setting Special Price</p>
                    <p className="mt-0.5">
                      Special price can be set for any plan without activating it.
                      {isUpdate && ' Updating existing special price.'}
                    </p>
                    <p className="mt-0.5 text-emerald-600">
                      ✓ Plan will NOT be activated automatically
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </form>

        {/* Footer */}
        <div className="flex-shrink-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center gap-3 rounded-b-2xl">
          <div className="flex-1 flex items-center gap-2">
            {/* ✅ Remove Button - Always show if selected plan has special price */}
            {formData.planId && selectedPlanHasSpecialPrice && (
              <button
                type="button"
                onClick={() => handleRemoveSpecialPrice(formData.planId)}
                disabled={isSubmitting || isFetching || isRemovingSpecific === formData.planId}
                className="px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                {isRemovingSpecific === formData.planId ? 'Removing...' : 'Remove'}
              </button>
            )}
            
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting || isFetching || isRemovingSpecific === formData.planId}
              className={`flex-1 px-6 py-2.5 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${
                isUpdate ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-indigo-600 hover:bg-indigo-700'
              }`}
            >
              <DollarSign className="w-4 h-4" />
              {isSubmitting ? 'Saving...' : isUpdate ? 'Update Special Price' : 'Set Special Price'}
            </button>
          </div>
          
          <button
            type="button"
            onClick={onClose}
            disabled={isRemovingSpecific === formData.planId}
            className="px-6 py-2.5 border border-slate-200 text-slate-600 text-sm font-medium rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default SetSpecialPriceModal;